import { memo, useEffect, useMemo, useState } from 'react';
import {
	calculateProportionalSize,
	COLLECTIONS,
	findNearestLargerSize,
	getCollection,
	getImageById,
} from '~/assets/collections';
import { Spinner } from '~/components/ui/Spinner';
import { rage } from '~/rage';
import { StyledImage, StyledSpinnerContainer } from './CollectionImage.styled';

const CollectionImage = <T extends keyof typeof COLLECTIONS>({
	collection,
	id,
	size,
	objectFit = 'contain',
	...props
}: {
	collection: T;
	id: (typeof COLLECTIONS)[T]['contents'][number];
	size: keyof (typeof COLLECTIONS)[T]['sizes'] | number;
	objectFit?: 'contain' | 'cover';
} & React.HTMLAttributes<HTMLDivElement>) => {
	const [image, setImage] = useState<null | string>(null);
	const collectionData = useMemo(() => getCollection(collection), [collection]);

	const nearestSize = useMemo(
		() => typeof size === 'number' && findNearestLargerSize(collection, size),
		[collection, size],
	);

	const selectedSize = useMemo(() => {
		if (nearestSize) {
			return collectionData.sizes[
				nearestSize as keyof typeof collectionData.sizes
			];
		}
		return collectionData.sizes[size as keyof typeof collectionData.sizes];
	}, [collectionData, nearestSize, size]);

	const displaySize = useMemo(
		() =>
			nearestSize && typeof size === 'number'
				? calculateProportionalSize<T>(
						collection,
						nearestSize as keyof typeof collectionData.sizes,
						size,
				  )
				: selectedSize,
		[collection, nearestSize, size, selectedSize],
	);

	const imageSize = useMemo(
		() => nearestSize || (size as keyof typeof collectionData.sizes),
		[nearestSize, size],
	);

	useEffect(() => {
		let cancelled = false;
		setImage(null);

		const fetchImage = async () => {
			try {
				const loadedImage = await getImageById(collection, id, imageSize);
				if (!cancelled) {
					setImage(loadedImage);
				}
			} catch (error) {
				rage.notifications.showException(error);
			}
		};

		fetchImage().catch((err) => rage.notifications.showException(err));

		return () => {
			cancelled = true;
		};
	}, [collection, id, imageSize]);

	if (!image) {
		return (
			<div {...props} className="collection-spinner">
				<StyledSpinnerContainer
					style={{
						width: displaySize.w,
						height: displaySize.h,
						margin: '0 auto',
					}}
				>
					<Spinner />
				</StyledSpinnerContainer>
			</div>
		);
	}

	return (
		<StyledImage
			width={displaySize.w}
			height={displaySize.h}
			src={image || ''}
			$objectFit={objectFit}
			{...props}
		/>
	);
};

const MemoizedCollectionImage = memo(CollectionImage) as typeof CollectionImage;

export { MemoizedCollectionImage as CollectionImage };
